import classNames         from 'classnames';
import * as React         from 'react';
import { createPortal }   from 'react-dom';
import { useTranslation } from 'react-i18next';
import DV                 from 'ts-data-validator';

import SingleSelect from '../../../fields/select/SingleSelect';
import CloseIcon    from '../../../icons/CloseIcon';

const UserLocationModal = React.forwardRef(function(props, ref) {
  const { t } = useTranslation();

  const {
          changeLocation,
          sub_title,
          title,
          user,
        } = props;

  const modalRef = React.useRef(null);

  const [city, setCity]     = React.useState();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isShow, setIsShow] = React.useState(false);
  const [query, setQuery]   = React.useState('');
  const [root, setRoot]     = React.useState(null);

  const filteredOptions = React.useMemo(() => {
    return user.cities.filter((option) => {
      return option.label
        .toLowerCase()
        .includes(query.toLocaleLowerCase());
    });
  }, [query, user.cities]);
  const options         = React.useMemo(() => {
    const sortedOptions = user.cities.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });

    const optionsWithHeaders = [];
    let currentLetter        = '';

    for (let option of sortedOptions) {
      const letter = option.label
        .charAt(0)
        .toUpperCase();

      if (letter !== currentLetter) {
        optionsWithHeaders.push({
          cityId:   letter,
          isHeader: true,
          label:    letter,
        });

        currentLetter = letter;
      }

      optionsWithHeaders.push(option);
    }

    optionsWithHeaders.push({
      cityId: null,
      label:  t('client_my_city_is_not_listed'),
    });

    return optionsWithHeaders;
  }, [user.cities]);

  const onHideModal        = React.useCallback(() => {
    let cityId = user.cities.find((city) => city.isCapital)?.cityId;

    setQuery('');

    if (DV.isNumber(user.location?.cityId) && DV.isNotEmptyString(user.location?.label)) {
      cityId = user.location.cityId;
    }

    if (isShow) {
      changeLocation(cityId);

      setIsShow(false);
    }
  }, [changeLocation, isShow, user.cities, user.location]);
  const confirm            = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const newCityId = city ?? user.cities.find((city) => city.isCapital)?.cityId;

    if (isShow && newCityId) {
      changeLocation(newCityId);

      onHideModal();
    }
  }, [changeLocation, city, isShow, onHideModal, user.cities]);
  const handleChangeCity   = React.useCallback((city) => {
    setCity(city);
  }, []);
  const handleKeyDown      = React.useCallback((event) => {
    if (event.key === 'Escape' && isOpen && isShow) {
      onHideModal();
    }
  }, [isOpen, isShow, onHideModal]);
  const onShowModal        = React.useCallback(() => {
    setIsOpen(true);
  }, []);
  const handleClickOutside = React.useCallback((event) => {
    if (isShow && modalRef.current && !modalRef.current.contains(event.target)) {
      onHideModal();
    }
  }, [isShow, onHideModal]);

  React.useEffect(() => {
    setRoot(document.getElementById('modal-root'));

    return () => {
      document.body.classList.remove('fixed');

      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);

      setRoot(null);
    };
  }, []);
  React.useEffect(() => {
    let city;
    let show;

    if (isOpen) {
      if (!isShow) {
        show = true;

        if (!!user.location && DV.isNumber(user.location.cityId) && DV.isNotEmptyString(user.location.label)) {
          city = user.location.cityId;
        }
      }
    }

    if (typeof city !== 'undefined' || typeof show !== 'undefined') {
      setTimeout(() => {
        setCity(city);
        setIsShow(show);
      }, 250);
    }
  }, [isOpen, user.location]);
  React.useEffect(() => {
    if (!isShow) {
      setTimeout(() => {
        setIsOpen(false);
      }, 250);
    }
  }, [isShow]);
  React.useEffect(() => {
    document.body.classList.toggle('fixed', isShow && isOpen);

    if (isShow && isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isShow]);

  React.useImperativeHandle(ref, () => {
    return { onShowModal };
  });

  if (!root || !isOpen) {
    return null;
  }

  return createPortal((
    <div className={classNames('modal-overlay', { show: isShow })}>
      <div
        className={classNames('modal user_location_modal', {
          'no-padding': !DV.isNotEmptyString(title),
        })}
        ref={modalRef}
      >
        {DV.isNotEmptyString(title) && (
          <section className={'head'}>
            <div className={'head_title'}>{t(title)}</div>
            <div className={'head_subtitle'}>{t(sub_title)}</div>
            <span
              className={'close'}
              onClick={onHideModal}
            ><CloseIcon /></span>
          </section>
        )}
        <section className={'body user-location'}>
          <div>
            <SingleSelect
              isOpened
              onChangeCallback={handleChangeCity}
              onSearch={setQuery}
              options={options}
              placeholder={''}
              required
              search
              selected={city}
              valueKey={'cityId'}
            />
          </div>
        </section>
        <section className={'footer'}>
          {DV.isNumber(city) && filteredOptions.length > 0
            ? (
              <>
                <span
                  className={classNames('confirm', { disabled: !city })}
                  onClick={confirm}
                >{t('select')}</span>
                <span className={'modal-footer-signature'}>{t('client_chooses_city')}</span>
              </>
            )
            : <span className={'modal-city-not-found'}>{t('client_city_not_found')}</span>
          }
        </section>
      </div>
    </div>
  ), root);
});

export default UserLocationModal;
